import {
  $glimrTagsAtom,
  $ppidAtom,
  $pageViewIdAtom
} from '@client/core/atoms/config.js';
import { $gamTargetingAtom } from '@client/core/atoms/gamTargeting.js';
import { getGlimrTags } from '@client/core/utils/glimr.js';
import {
  debugLog,
  type GamConfig,
  UNLEASH_FEATURE_NAME
} from '@schibsted-nmp/advertising-shared';
import { isFeatureEnabled } from '@client/core/atoms/unleashFeatures.js';
import { isFinnTravelResultPage } from '@client/core/utils/travel/isFinnTravelResultPage.js';

import { setTargetingOnSlotOrGlobal } from './targeting.js';
import { Api } from './Api/index.js';
import * as EventListeners from './EventListeners/index.js';
import { LazyLoad } from './LazyLoad.js';

export async function initiateGam(
  gamConfig: GamConfig,
  ...abortSignals: ReadonlyArray<AbortSignal>
) {
  await EventListeners.setup(...abortSignals);

  const googletag = await Api(...abortSignals);

  googletag.pubads().enableSingleRequest();
  googletag.pubads().collapseEmptyDivs(true);
  googletag.pubads().disableInitialLoad();
  googletag.pubads().setCentering(true);

  if (
    isFeatureEnabled(UNLEASH_FEATURE_NAME.enableGamLazyLoading) &&
    !isFinnTravelResultPage()
  ) {
    await LazyLoad.enable(...abortSignals);
  }

  // Set global targeting
  $ppidAtom.subscribe(initiateGam.onPpidChange);
  $glimrTagsAtom.subscribe(initiateGam.onGlimrTagsChange);
  $pageViewIdAtom.subscribe(initiateGam.onPageViewIdChange);
  $gamTargetingAtom.subscribe(initiateGam.onGamTargetingChange);

  // Enable services now that everything is set up
  googletag.enableServices();

  debugLog('Google Ad Manager initialized', googletag, gamConfig);
}

export namespace initiateGam {
  export function onPpidChange({ delta }: $ppidAtom.Value) {
    if (!delta) return;

    const ppid = delta.ppId1 || delta.ppId2;
    if (!ppid) return;

    try {
      debugLog('Setting PPID on Gam', ppid);
      googletag.pubads().setPublisherProvidedId(ppid);
    } catch (error) {
      debugLog('Error setting PPID on GAM', error);
    }
  }

  export function onGlimrTagsChange(glimrTags: $glimrTagsAtom.Value) {
    const targeting = getGlimrTags(glimrTags);

    debugLog('Setting GLIMR targeting on GAM', targeting);

    setTargetingOnSlotOrGlobal({ targeting, global: true }).catch(
      (error: unknown) => {
        debugLog('Error setting GLIMR targeting on GAM', error);
      }
    );
  }

  export function onGamTargetingChange(targeting: $gamTargetingAtom.Value) {
    setTargetingOnSlotOrGlobal({ targeting, global: true }).catch(
      (error: unknown) => {
        debugLog('Error setting global targeting on GAM', error);
      }
    );
  }

  export function onPageViewIdChange(pageViewId: $pageViewIdAtom.Value) {
    if (!pageViewId) return;

    debugLog('Setting PageViewId on GAM', pageViewId);

    setTargetingOnSlotOrGlobal({
      targeting: [{ key: 'PageViewId', value: [pageViewId] }],
      global: true
    }).catch((error: unknown) => {
      debugLog('Error setting PageViewId on GAM', error);
    });
  }
}
